// src/Components/CommunitySettingsModal/CommunitySchemasTab.tsx
//
// DEV-875 — Schema editor UI. Lives inside CommunitySettingsModal as a new
// "Schemas" tab. Visible only when the selected community is an org root.
// Uses the org-scoped CRUD endpoints from DEV-874.
//
// Layout: two-pane grid. Left pane lists schemas with "Projects Using"
// counts; right pane shows fields of the selected schema. Both panes are
// self-contained cards with their own header + actions row. Modals (schema
// editor, field editor, confirm) overlay the modal.
//
// Edit semantics enforced server-side per ADR-004 §Decision 8:
//   - field_key is never editable
//   - type changes blocked when populated (409)
//   - schema/field deletes blocked when populated (409)

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import styles from "./CommunitySettingsModal.module.css";
import {
  fetchSchemasForOrg,
  createSchemaForOrg,
  updateSchema,
  deleteSchema,
  addSchemaField,
  updateSchemaField,
  deleteSchemaField,
  fetchFieldsForSchema,
  fetchAllSchemaFields,
  fetchAllHierarchies,
  deleteSchemaHierarchy,
} from "../../api";
import type {
  CollectionSchemaResponse,
  CollectionSchemaFieldResponse,
  SchemaHierarchyResponse,
} from "../../api";
import { SchemaHierarchyEditor } from "./SchemaHierarchyEditor";

interface Props {
  /** The org-root community guid this tab is editing schemas for. */
  communityGuid: string;
  /** When true, this tab is rendered. Caller already gates this on "is org root". */
  enabled: boolean;
  /**
   * DEV-920. When true, the editor exposes platform-schema CRUD (rename,
   * delete, add/edit/delete fields, add/edit/delete hierarchies). The
   * server enforces "platform = sysadmin only" at the API layer regardless
   * of this flag; this prop just unhides the controls so a sysadmin can
   * actually use them. Non-sysadmins see platform schemas as read-only.
   */
  isSystemAdmin: boolean;
}

const FIELD_TYPES = [
  { value: "string", labelKey: "schemaEditor.type.string" },
  { value: "number", labelKey: "schemaEditor.type.number" },
  { value: "boolean", labelKey: "schemaEditor.type.boolean" },
  { value: "date", labelKey: "schemaEditor.type.date" },
  { value: "enum", labelKey: "schemaEditor.type.enum" },
  { value: "guid", labelKey: "schemaEditor.type.guid" },
];

type EditingSchema = {
  schemaGuid: string | null; // null = new
  schemaName: string;
};

type EditingField = {
  schemaGuid: string;
  fieldGuid: string | null; // null = new
  fieldKey: string;
  label: string;
  type: string;
  groupable: boolean;
  sortable: boolean;
  searchable: boolean;
  missingPolicy: string;
  enumSetGuid: string | null;
  entityType: string | null;
};

type ConfirmAction = {
  title: string;
  detail: string;
  onConfirm: () => Promise<void> | void;
};

const CommunitySchemasTab: React.FC<Props> = ({ communityGuid, enabled, isSystemAdmin }) => {
  const { t } = useAppTranslation("community");

  const [schemas, setSchemas] = useState<CollectionSchemaResponse[]>([]);
  const [selectedSchemaGuid, setSelectedSchemaGuid] = useState<string | null>(null);
  const [fields, setFields] = useState<CollectionSchemaFieldResponse[]>([]);

  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editingSchema, setEditingSchema] = useState<EditingSchema | null>(null);
  const [editingField, setEditingField] = useState<EditingField | null>(null);
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);

  // DEV-920 (C1): per-schema field and hierarchy counts surfaced in the
  // schemas list. Computed client-side from one fetchAll round-trip per
  // concept; refetched after any mutation that could shift them. Keys are
  // schemaGuid lowercase to dodge case mismatch between API responses.
  const [fieldCounts, setFieldCounts] = useState<Record<string, number>>({});
  const [hierarchiesBySchema, setHierarchiesBySchema] = useState<
    Record<string, SchemaHierarchyResponse[]>
  >({});

  // DEV-920 (C2): hierarchy create/edit modal state. null = closed.
  const [editingHierarchy, setEditingHierarchy] = useState<{
    mode: "create" | "edit";
    existing?: SchemaHierarchyResponse;
  } | null>(null);

  const refreshSchemas = useCallback(async () => {
    if (!enabled || !communityGuid) return;
    setLoadingSchemas(true);
    setErrorMessage(null);
    try {
      const rows = await fetchSchemasForOrg(communityGuid);
      setSchemas(rows);
    } catch (err: any) {
      setErrorMessage(err?.message || t("schemaEditor.errors.loadFailed"));
    } finally {
      setLoadingSchemas(false);
    }
  }, [communityGuid, enabled, t]);

  useEffect(() => {
    refreshSchemas();
  }, [refreshSchemas]);

  const refreshFields = useCallback(async (schemaGuid: string) => {
    setLoadingFields(true);
    try {
      const rows = await fetchFieldsForSchema(schemaGuid);
      setFields(rows.sort((a, b) => a.label.localeCompare(b.label)));
    } catch (err: any) {
      setErrorMessage(err?.message || t("schemaEditor.errors.loadFieldsFailed"));
    } finally {
      setLoadingFields(false);
    }
  }, [t]);

  useEffect(() => {
    if (selectedSchemaGuid) {
      refreshFields(selectedSchemaGuid);
    } else {
      setFields([]);
    }
  }, [selectedSchemaGuid, refreshFields]);

  // DEV-920 (C1). Refresh per-schema field + hierarchy counts. Run on mount
  // and after any mutation that could shift them. Two parallel GETs; cheap.
  const refreshAllCounts = useCallback(async () => {
    if (!enabled) return;
    try {
      const [allFields, allHierarchies] = await Promise.all([
        fetchAllSchemaFields(),
        fetchAllHierarchies(),
      ]);
      const fc: Record<string, number> = {};
      for (const f of allFields) {
        const k = f.schemaGuid.toLowerCase();
        fc[k] = (fc[k] ?? 0) + 1;
      }
      const hm: Record<string, SchemaHierarchyResponse[]> = {};
      for (const h of allHierarchies) {
        const k = h.schemaGuid.toLowerCase();
        (hm[k] ??= []).push(h);
      }
      setFieldCounts(fc);
      setHierarchiesBySchema(hm);
    } catch (err: any) {
      // Counts are presentation-only; don't blow up the editor on a count fetch
      // failure. Surface in the error banner so the admin knows something
      // partial is going on.
      setErrorMessage(err?.message || t("schemaEditor.errors.loadFailed"));
    }
  }, [enabled, t]);

  useEffect(() => {
    refreshAllCounts();
  }, [refreshAllCounts]);

  const selectedSchema = useMemo(
    () => schemas.find((s) => s.schemaGuid === selectedSchemaGuid) ?? null,
    [schemas, selectedSchemaGuid],
  );

  const isPlatform = !!selectedSchema?.isPlatform;
  // DEV-920. Sysadmins can edit platform schemas (matches the server-side
  // CallerCanAdminSchema rule). Non-sysadmins only see platform schemas as
  // read-only, mirroring DEV-875's original UI behaviour.
  const canEditSelected = !!selectedSchema && (!isPlatform || isSystemAdmin);

  // ── Schema operations ──────────────────────────────────────

  const onCreateSchema = () => {
    setEditingSchema({ schemaGuid: null, schemaName: "" });
  };

  const onEditSchema = (s: CollectionSchemaResponse) => {
    if (s.isPlatform && !isSystemAdmin) return;
    setEditingSchema({ schemaGuid: s.schemaGuid, schemaName: s.schemaName });
  };

  const onSaveSchema = async () => {
    if (!editingSchema) return;
    const name = editingSchema.schemaName.trim();
    if (!name) {
      setErrorMessage(t("schemaEditor.errors.nameRequired"));
      return;
    }
    try {
      if (editingSchema.schemaGuid === null) {
        const created = await createSchemaForOrg(communityGuid, { schemaName: name });
        setSelectedSchemaGuid(created.schemaGuid);
      } else {
        await updateSchema(editingSchema.schemaGuid, { schemaName: name });
      }
      setEditingSchema(null);
      await Promise.all([refreshSchemas(), refreshAllCounts()]);
    } catch (err: any) {
      setErrorMessage(err?.message || t("schemaEditor.errors.saveFailed"));
    }
  };

  const onDeleteSchema = (s: CollectionSchemaResponse) => {
    if (s.isPlatform && !isSystemAdmin) return;
    setConfirm({
      title: t("schemaEditor.confirm.deleteSchemaTitle"),
      detail: t("schemaEditor.confirm.deleteSchemaDetail", { name: s.schemaName }),
      onConfirm: async () => {
        try {
          await deleteSchema(s.schemaGuid);
          if (selectedSchemaGuid === s.schemaGuid) setSelectedSchemaGuid(null);
          await Promise.all([refreshSchemas(), refreshAllCounts()]);
          setConfirm(null);
        } catch (err: any) {
          setErrorMessage(err?.message || t("schemaEditor.errors.deleteFailed"));
          setConfirm(null);
        }
      },
    });
  };

  // ── Field operations ───────────────────────────────────────

  const onCreateField = () => {
    if (!selectedSchemaGuid) return;
    setEditingField({
      schemaGuid: selectedSchemaGuid,
      fieldGuid: null,
      fieldKey: "",
      label: "",
      type: "string",
      // DEV-919 followup: groupable defaults to true. New custom fields are
      // useful in the portfolio Group By dropdown by default — opt-out, not
      // opt-in. The previous default (false) silently hid every admin-created
      // field from the dropdown until an admin remembered to toggle it on,
      // which broke the "select a collection, group by its custom fields"
      // workflow. Sortable/searchable stay false — those have indexing
      // implications and should remain explicit opt-ins.
      groupable: true,
      sortable: false,
      searchable: false,
      missingPolicy: "treat_as_unknown",
      enumSetGuid: null,
      entityType: null,
    });
  };

  const onEditField = (f: CollectionSchemaFieldResponse) => {
    if (!canEditSelected) return;
    setEditingField({
      schemaGuid: f.schemaGuid,
      fieldGuid: f.fieldGuid,
      fieldKey: f.fieldKey,
      label: f.label,
      type: f.type,
      groupable: f.groupable,
      sortable: f.sortable,
      searchable: f.searchable,
      missingPolicy: f.missingPolicy,
      enumSetGuid: f.enumSetGuid,
      entityType: f.entityType,
    });
  };

  const onSaveField = async () => {
    if (!editingField) return;
    const ef = editingField;
    if (!ef.label.trim()) {
      setErrorMessage(t("schemaEditor.errors.fieldLabelRequired"));
      return;
    }
    if (ef.fieldGuid === null && !ef.fieldKey.trim()) {
      setErrorMessage(t("schemaEditor.errors.fieldKeyRequired"));
      return;
    }
    if (ef.type === "enum" && !ef.enumSetGuid) {
      setErrorMessage(t("schemaEditor.errors.enumNeedsSet"));
      return;
    }
    if (ef.type === "guid" && !ef.entityType?.trim()) {
      setErrorMessage(t("schemaEditor.errors.guidNeedsEntityType"));
      return;
    }

    try {
      if (ef.fieldGuid === null) {
        await addSchemaField(ef.schemaGuid, {
          fieldKey: ef.fieldKey.trim(),
          label: ef.label.trim(),
          type: ef.type,
          groupable: ef.groupable,
          sortable: ef.sortable,
          searchable: ef.searchable,
          missingPolicy: ef.missingPolicy,
          enumSetGuid: ef.enumSetGuid,
          entityType: ef.entityType,
        });
      } else {
        await updateSchemaField(ef.schemaGuid, ef.fieldGuid, {
          // field_key intentionally omitted — server will not accept it
          label: ef.label.trim(),
          type: ef.type,
          groupable: ef.groupable,
          sortable: ef.sortable,
          searchable: ef.searchable,
          missingPolicy: ef.missingPolicy,
          enumSetGuid: ef.enumSetGuid,
          entityType: ef.entityType,
        });
      }
      setEditingField(null);
      await Promise.all([
        refreshFields(ef.schemaGuid),
        refreshSchemas(), // usage counts may shift
        refreshAllCounts(),
      ]);
    } catch (err: any) {
      setErrorMessage(err?.message || t("schemaEditor.errors.saveFailed"));
    }
  };

  // ── Hierarchy operations (DEV-920 C2) ───────────────────────

  const onCreateHierarchy = () => {
    if (!selectedSchemaGuid) return;
    setEditingHierarchy({ mode: "create" });
  };

  const onEditHierarchy = (h: SchemaHierarchyResponse) => {
    if (!canEditSelected) return;
    setEditingHierarchy({ mode: "edit", existing: h });
  };

  const onDeleteHierarchy = (h: SchemaHierarchyResponse) => {
    if (!canEditSelected) return;
    setConfirm({
      title: t("schemaEditor.confirm.deleteHierarchyTitle"),
      detail: t("schemaEditor.confirm.deleteHierarchyDetail", {
        name: h.displayName,
      }),
      onConfirm: async () => {
        try {
          await deleteSchemaHierarchy(h.schemaGuid, h.hierarchyGuid);
          await refreshAllCounts();
          setConfirm(null);
        } catch (err: any) {
          setErrorMessage(err?.message || t("schemaEditor.errors.deleteFailed"));
          setConfirm(null);
        }
      },
    });
  };

  const onDeleteField = (f: CollectionSchemaFieldResponse) => {
    if (!canEditSelected) return;
    setConfirm({
      title: t("schemaEditor.confirm.deleteFieldTitle"),
      detail: t("schemaEditor.confirm.deleteFieldDetail", { name: f.label }),
      onConfirm: async () => {
        try {
          await deleteSchemaField(f.schemaGuid, f.fieldGuid);
          await Promise.all([
            refreshFields(f.schemaGuid),
            refreshSchemas(),
            refreshAllCounts(),
          ]);
          setConfirm(null);
        } catch (err: any) {
          setErrorMessage(err?.message || t("schemaEditor.errors.deleteFailed"));
          setConfirm(null);
        }
      },
    });
  };

  if (!enabled) return null;

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className={styles.schemaEditorSection}>
      <h2 className={styles.schemaEditorPaneTitle} style={{ fontSize: 16, marginBlockEnd: 6 }}>
        {t("schemaEditor.title")}
      </h2>
      <p className={styles.schemaEditorIntro}>{t("schemaEditor.subtitle")}</p>

      {errorMessage && (
        <div className={styles.schemaEditorErrorBanner}>
          <span>{errorMessage}</span>
          <button type="button" onClick={() => setErrorMessage(null)}>
            {t("common.dismiss")}
          </button>
        </div>
      )}

      <div className={styles.schemaEditorLayout}>
        {/* ── Left pane: schema list ────────────────────── */}
        <div className={styles.schemaEditorPane}>
          <div className={styles.schemaEditorPaneHeader}>
            <h3 className={styles.schemaEditorPaneTitle}>
              {t("schemaEditor.schemasHeading")}
            </h3>
            <div className={styles.schemaEditorActions}>
              <button
                type="button"
                className={`${styles.schemaEditorBtn} ${styles.schemaEditorBtnPrimary}`}
                onClick={onCreateSchema}
              >
                + {t("schemaEditor.newSchema")}
              </button>
            </div>
          </div>

          {loadingSchemas ? (
            <div className={styles.schemaEditorPlaceholder}>{t("common.loading")}</div>
          ) : schemas.length === 0 ? (
            <div className={styles.schemaEditorPlaceholder}>{t("schemaEditor.empty")}</div>
          ) : (
            <table className={styles.schemaEditorTable}>
              <thead>
                <tr>
                  <th>{t("schemaEditor.col.name")}</th>
                  <th className={styles.schemaEditorColNumber}>
                    {t("schemaEditor.col.fields")}
                  </th>
                  <th className={styles.schemaEditorColNumber}>
                    {t("schemaEditor.col.hierarchies")}
                  </th>
                  <th className={styles.schemaEditorColNumber}>
                    {t("schemaEditor.col.projectsUsing")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {schemas.map((s) => {
                  const isSel = s.schemaGuid === selectedSchemaGuid;
                  const sk = s.schemaGuid.toLowerCase();
                  const fc = fieldCounts[sk] ?? 0;
                  const hc = hierarchiesBySchema[sk]?.length ?? 0;
                  return (
                    <tr
                      key={s.schemaGuid}
                      className={isSel ? styles.schemaEditorRowSelected : undefined}
                      onClick={() => setSelectedSchemaGuid(s.schemaGuid)}
                    >
                      <td>
                        {s.schemaName}
                        {s.isPlatform && (
                          <span className={styles.schemaEditorBadge}>
                            {t("schemaEditor.platformBadge")}
                          </span>
                        )}
                      </td>
                      <td className={styles.schemaEditorColNumber}>{fc}</td>
                      <td className={styles.schemaEditorColNumber}>{hc}</td>
                      <td className={styles.schemaEditorColNumber}>{s.projectsUsing}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Right pane: fields of selected schema ─────── */}
        <div className={styles.schemaEditorPane}>
          <div className={styles.schemaEditorPaneHeader}>
            <h3 className={styles.schemaEditorPaneTitle}>
              {selectedSchema
                ? t("schemaEditor.fieldsFor", { name: selectedSchema.schemaName })
                : t("schemaEditor.selectSchemaPrompt")}
            </h3>
            {selectedSchema && (
              <div className={styles.schemaEditorActions}>
                {canEditSelected ? (
                  <>
                    <button
                      type="button"
                      className={styles.schemaEditorBtn}
                      onClick={() => onEditSchema(selectedSchema)}
                    >
                      {t("schemaEditor.renameSchema")}
                    </button>
                    <button
                      type="button"
                      className={`${styles.schemaEditorBtn} ${styles.schemaEditorBtnDanger}`}
                      onClick={() => onDeleteSchema(selectedSchema)}
                    >
                      {t("schemaEditor.deleteSchema")}
                    </button>
                    <button
                      type="button"
                      className={`${styles.schemaEditorBtn} ${styles.schemaEditorBtnPrimary}`}
                      onClick={onCreateField}
                    >
                      + {t("schemaEditor.newField")}
                    </button>
                  </>
                ) : (
                  <span className={styles.schemaEditorPlatformNote}>
                    {t("schemaEditor.platformReadOnly")}
                  </span>
                )}
              </div>
            )}
          </div>

          {!selectedSchema ? null : loadingFields ? (
            <div className={styles.schemaEditorPlaceholder}>{t("common.loading")}</div>
          ) : fields.length === 0 ? (
            <div className={styles.schemaEditorPlaceholder}>
              {t("schemaEditor.noFields")}
            </div>
          ) : (
            <table className={styles.schemaEditorTable}>
              <thead>
                <tr>
                  <th>{t("schemaEditor.col.label")}</th>
                  <th>{t("schemaEditor.col.key")}</th>
                  <th>{t("schemaEditor.col.type")}</th>
                  <th>{t("schemaEditor.col.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f) => (
                  <tr key={f.fieldGuid} onClick={(e) => e.stopPropagation()}>
                    <td>{f.label}</td>
                    <td>
                      <code className={styles.schemaEditorFieldKey}>{f.fieldKey}</code>
                    </td>
                    <td className={styles.schemaEditorTypeCell}>
                      {f.type}
                      {f.entityType ? `: ${f.entityType}` : ""}
                    </td>
                    <td>
                      {canEditSelected && (
                        <>
                          <button
                            type="button"
                            className={styles.schemaEditorBtn}
                            onClick={() => onEditField(f)}
                          >
                            {t("common.edit")}
                          </button>{" "}
                          <button
                            type="button"
                            className={`${styles.schemaEditorBtn} ${styles.schemaEditorBtnDanger}`}
                            onClick={() => onDeleteField(f)}
                          >
                            {t("common.delete")}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* ── Hierarchies section (DEV-920 C1) ────────── */}
          {selectedSchema && canEditSelected && (
            <div style={{ marginBlockStart: 16 }}>
              <div className={styles.schemaEditorPaneHeader}>
                <h4
                  className={styles.schemaEditorPaneTitle}
                  style={{ fontSize: 14 }}
                >
                  {t("schemaEditor.hierarchiesHeading")}
                </h4>
                <div className={styles.schemaEditorActions}>
                  <button
                    type="button"
                    className={`${styles.schemaEditorBtn} ${styles.schemaEditorBtnPrimary}`}
                    onClick={onCreateHierarchy}
                  >
                    + {t("schemaEditor.newHierarchy")}
                  </button>
                </div>
              </div>
              {(() => {
                const hs =
                  hierarchiesBySchema[
                    selectedSchema.schemaGuid.toLowerCase()
                  ] ?? [];
                if (hs.length === 0) {
                  return (
                    <div className={styles.schemaEditorPlaceholder}>
                      {t("schemaEditor.noHierarchies")}
                    </div>
                  );
                }
                return (
                  <table className={styles.schemaEditorTable}>
                    <thead>
                      <tr>
                        <th>{t("schemaEditor.col.hierarchyName")}</th>
                        <th className={styles.schemaEditorColNumber}>
                          {t("schemaEditor.col.tiers")}
                        </th>
                        <th>{t("schemaEditor.col.source")}</th>
                        <th>{t("schemaEditor.col.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hs.map((h) => {
                        let tierCount = 0;
                        try {
                          const parsed = JSON.parse(h.levelOrder);
                          tierCount = Array.isArray(parsed)
                            ? parsed.length
                            : 0;
                        } catch {
                          /* malformed level_order — render 0 */
                        }
                        const isEntityWalk = h.valueSource === "entity_walk";
                        return (
                          <tr key={h.hierarchyGuid}>
                            <td>{h.displayName}</td>
                            <td className={styles.schemaEditorColNumber}>
                              {tierCount}
                            </td>
                            <td className={styles.schemaEditorTypeCell}>
                              {h.valueSource}
                            </td>
                            <td>
                              {!isEntityWalk && canEditSelected && (
                                <>
                                  <button
                                    type="button"
                                    className={styles.schemaEditorBtn}
                                    onClick={() => onEditHierarchy(h)}
                                  >
                                    {t("common.edit")}
                                  </button>{" "}
                                  <button
                                    type="button"
                                    className={`${styles.schemaEditorBtn} ${styles.schemaEditorBtnDanger}`}
                                    onClick={() => onDeleteHierarchy(h)}
                                  >
                                    {t("common.delete")}
                                  </button>
                                </>
                              )}
                              {isEntityWalk && (
                                <span
                                  className={styles.schemaEditorPlatformNote}
                                >
                                  {t("schemaEditor.platformReadOnly")}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* ── Schema editor modal ──────────────────────────── */}
      {editingSchema && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>
              {editingSchema.schemaGuid === null
                ? t("schemaEditor.modal.createSchema")
                : t("schemaEditor.modal.renameSchema")}
            </h3>
            <div className={styles.field}>
              <label className={styles.label}>{t("schemaEditor.col.name")}</label>
              <input
                type="text"
                value={editingSchema.schemaName}
                onChange={(e) =>
                  setEditingSchema({ ...editingSchema, schemaName: e.target.value })
                }
                maxLength={64}
                autoFocus
              />
            </div>
            <div className={styles.schemaEditorModalFooter}>
              <button
                type="button"
                className={styles.schemaEditorBtn}
                onClick={() => setEditingSchema(null)}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className={`${styles.schemaEditorBtn} ${styles.schemaEditorBtnPrimary}`}
                onClick={onSaveSchema}
              >
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Field editor modal ───────────────────────────── */}
      {editingField && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>
              {editingField.fieldGuid === null
                ? t("schemaEditor.modal.createField")
                : t("schemaEditor.modal.editField")}
            </h3>

            <div className={styles.field}>
              <label className={styles.label}>{t("schemaEditor.col.label")}</label>
              <input
                type="text"
                value={editingField.label}
                onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                maxLength={128}
                autoFocus
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>
                {t("schemaEditor.col.key")}
                {editingField.fieldGuid !== null && (
                  <span className={styles.schemaEditorPlatformNote} style={{ marginInlineStart: 8 }}>
                    ({t("schemaEditor.keyImmutable")})
                  </span>
                )}
              </label>
              <input
                type="text"
                value={editingField.fieldKey}
                disabled={editingField.fieldGuid !== null}
                onChange={(e) =>
                  setEditingField({ ...editingField, fieldKey: e.target.value })
                }
                maxLength={64}
                placeholder="e.g. rotary_zone"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>{t("schemaEditor.col.type")}</label>
              <select
                value={editingField.type}
                onChange={(e) =>
                  setEditingField({
                    ...editingField,
                    type: e.target.value,
                    enumSetGuid: e.target.value === "enum" ? editingField.enumSetGuid : null,
                    entityType: e.target.value === "guid" ? editingField.entityType : null,
                  })
                }
              >
                {FIELD_TYPES.map((ft) => (
                  <option key={ft.value} value={ft.value}>
                    {t(ft.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            {editingField.type === "enum" && (
              <div className={styles.field}>
                <label className={styles.label}>{t("schemaEditor.enumSetGuid")}</label>
                <input
                  type="text"
                  value={editingField.enumSetGuid ?? ""}
                  onChange={(e) =>
                    setEditingField({ ...editingField, enumSetGuid: e.target.value || null })
                  }
                  placeholder="enum_set_guid"
                />
              </div>
            )}

            {editingField.type === "guid" && (
              <div className={styles.field}>
                <label className={styles.label}>{t("schemaEditor.entityType")}</label>
                <input
                  type="text"
                  value={editingField.entityType ?? ""}
                  onChange={(e) =>
                    setEditingField({ ...editingField, entityType: e.target.value || null })
                  }
                  placeholder="e.g. org_rotary_district"
                />
                <div className={styles.schemaEditorPlatformNote} style={{ marginBlockStart: 4 }}>
                  {t("schemaEditor.entityTypeHint")}
                </div>
              </div>
            )}

            <div className={styles.schemaEditorCheckRow}>
              <label>
                <input
                  type="checkbox"
                  checked={editingField.groupable}
                  onChange={(e) =>
                    setEditingField({ ...editingField, groupable: e.target.checked })
                  }
                />
                {t("schemaEditor.groupable")}
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={editingField.sortable}
                  onChange={(e) =>
                    setEditingField({ ...editingField, sortable: e.target.checked })
                  }
                />
                {t("schemaEditor.sortable")}
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={editingField.searchable}
                  onChange={(e) =>
                    setEditingField({ ...editingField, searchable: e.target.checked })
                  }
                />
                {t("schemaEditor.searchable")}
              </label>
            </div>

            <div className={styles.schemaEditorModalFooter}>
              <button
                type="button"
                className={styles.schemaEditorBtn}
                onClick={() => setEditingField(null)}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className={`${styles.schemaEditorBtn} ${styles.schemaEditorBtnPrimary}`}
                onClick={onSaveField}
              >
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Hierarchy editor (DEV-920 C2) ────────────────── */}
      {editingHierarchy && selectedSchema && (
        <SchemaHierarchyEditor
          mode={editingHierarchy.mode}
          schemaGuid={selectedSchema.schemaGuid}
          schemaFields={fields}
          existing={editingHierarchy.existing}
          onClose={() => setEditingHierarchy(null)}
          onSaved={() => refreshAllCounts()}
        />
      )}

      {/* ── Confirmation modal ───────────────────────────── */}
      {confirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>{confirm.title}</h3>
            <p>{confirm.detail}</p>
            <div className={styles.schemaEditorModalFooter}>
              <button
                type="button"
                className={styles.schemaEditorBtn}
                onClick={() => setConfirm(null)}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className={`${styles.schemaEditorBtn} ${styles.schemaEditorBtnDanger}`}
                onClick={() => confirm.onConfirm()}
              >
                {t("common.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunitySchemasTab;
